import React, { useCallback } from 'react';
import { WizardStepProps, LeadSource } from '../../../types';
import { updateCampaign } from '../../../utils/campaignApi';
import { WizardCache } from '../../../utils/wizardCache';

const LeadAcquisitionMethodStep: React.FC<WizardStepProps> = ({
  state,
  updateState
}) => {
  const leadAcquisitionMethods = [
    {
      method: 'preset_filters' as LeadSource,
      title: 'Target Leads',
      description: 'We\'ll build your target leads list using the criteria you give us',
      icon: '🎯',
      recommended: true
    },
    {
      method: 'past_list' as LeadSource,
      title: 'Use a Previous Target Lead list',
      description: 'Reuse target leads from past campaigns',
      icon: '📁',
      recommended: false
    },
    {
      method: 'lead_generator' as LeadSource,
      title: 'Purchase leads from our database',
      description: 'Access our curated lead database',
      icon: '🔍',
      recommended: false
    },
    {
      method: 'csv_upload' as LeadSource,
      title: 'Upload your own lead list',
      description: 'Provide a CSV file with your leads',
      icon: '📋',
      recommended: false
    }
  ];

  const handleMethodSelect = useCallback(async (method: LeadSource) => {
    updateState({ lead_acquisition_method: method });
    
    // Immediately cache the selection for instant restoration
    if (state.campaign_id) {
      WizardCache.write(state.campaign_id, { 
        leadMethod: method,
        step: state.current_step 
      });
    }
    
    // Auto-save the selection to database for persistence
    // Only save if we have all required fields (ensures data is loaded)
    if (state.campaign_id && state.campaign_name && state.service_tier) {
      console.log('Auto-saving lead acquisition method selection:', method);
      try {
        // Send the full state to preserve all campaign data
        await updateCampaign(state.campaign_id, {
          ...state, // Include all existing state
          lead_acquisition_method: method, // Update the method
          target_audience_type: method // Keep for backwards compatibility
        });
        console.log('Lead acquisition method saved successfully');
      } catch (error) {
        console.error('Failed to auto-save lead acquisition method:', error);
        // Don't block the UI - selection is still saved in local state
      }
    } else {
      console.log('Skipping auto-save - campaign data not yet loaded', {
        campaign_id: state.campaign_id,
        campaign_name: state.campaign_name,
        service_tier: state.service_tier
      });
    }
  }, [state.campaign_id, state.campaign_name, state.service_tier, updateState]);

  // Navigation handled by parent wizard component

  return (
    <div className="p-8">
      <div className="max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {leadAcquisitionMethods.map((method) => (
            <div
              key={method.method}
              onClick={() => handleMethodSelect(method.method)}
              className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                state.lead_acquisition_method === method.method
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {method.recommended && (
                <div className="absolute -top-3 left-4">
                  <span className="bg-green-500 text-white px-3 py-1 text-xs font-medium rounded-full">
                    Recommended
                  </span>
                </div>
              )}

              <div className="flex items-center mb-4">
                <span className="text-3xl mr-4">{method.icon}</span>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{method.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{method.description}</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 transition-colors ${
                  state.lead_acquisition_method === method.method
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {state.lead_acquisition_method === method.method && (
                    <svg className="w-4 h-4 text-white ml-0.5 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default LeadAcquisitionMethodStep;